import { useState, useEffect, useCallback } from 'react';
import { Coins, Plus, RefreshCw, CreditCard, Download } from 'lucide-react';
import { walletApi } from '../api/httpClient';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';

/** Dynamically load Razorpay checkout script (idempotent). */
function loadRazorpay() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// ── Transaction icon + label map ──────────────────────────────────────────────
const TX_META = {
  PURCHASE:             { icon: '🛒', label: 'Token top-up',                bg: 'var(--success-bg)',  color: 'var(--success)',   plus: true  },
  REGISTRATION:         { icon: '🎟', label: 'Attendee registration',       bg: 'var(--primary-50)', color: 'var(--primary-700)', plus: false },
  REFUND:               { icon: '↩️', label: 'Registration refund',          bg: 'var(--success-bg)', color: 'var(--success)',   plus: true  },
  BRANDED_EMAIL:        { icon: '✉️', label: 'Branded confirmation email',   bg: 'var(--violet-50)',  color: 'var(--violet)',    plus: false },
  LEAD_CAPTURE_ENABLE:  { icon: '🎯', label: 'Lead capture enabled',         bg: 'var(--violet-50)',  color: 'var(--violet)',    plus: false },
  BULK_IMPORT:          { icon: '📥', label: 'Bulk attendee import',          bg: 'var(--surface-2)',  color: 'var(--ink-3)',     plus: false },
  QR_EXPORT:            { icon: '📄', label: 'QR batch export',              bg: 'var(--warning-bg)', color: 'var(--warning)',   plus: false },
  CUSTOM_BRANDING:      { icon: '🎨', label: 'Custom branding',              bg: 'var(--violet-50)',  color: 'var(--violet)',    plus: false },
  ADJUSTMENT:           { icon: '⚙️', label: 'Manual adjustment',            bg: 'var(--surface-2)',  color: 'var(--ink-3)',     plus: false },
  TRIAL:                { icon: '🎁', label: 'Trial credits',                bg: 'var(--success-bg)', color: 'var(--success)',   plus: true  },
  AUTO_TOPUP:           { icon: '🔄', label: 'Auto top-up',                 bg: 'var(--success-bg)', color: 'var(--success)',   plus: true  },
  WHATSAPP:             { icon: '💬', label: 'WhatsApp reminders',           bg: 'var(--success-bg)', color: 'var(--success)',   plus: false },
};

// ── Usage row ─────────────────────────────────────────────────────────────────
function UsageRow({ tx, index }) {
  const isCredit = tx.type === 'CREDIT';
  const meta     = TX_META[tx.reference_type] || { icon: '🪙', label: tx.reference_type, bg: 'var(--surface-2)', color: 'var(--ink-3)', plus: false };
  const isPlus   = meta.plus || isCredit;

  return (
    <div className="usage">
      <div className="usage-ic" style={{ background: meta.bg, color: meta.color }}>
        <span style={{ fontSize: 17 }}>{meta.icon}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="usage-nm">{meta.label}</div>
        {tx.notes && <div className="usage-mt">{tx.notes}</div>}
        <div className="usage-mt">
          {new Date(tx.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      </div>
      <div className={`usage-amt ${isPlus ? 'plus' : 'minus'}`}>
        {isPlus ? '+' : '−'}{tx.amount.toLocaleString()}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function TokenWallet() {
  const [wallet, setWallet]       = useState(null);
  const [packages, setPackages]   = useState([]);
  const [selectedPkg, setSelected] = useState(null);   // id of selected pack
  const [buyingPkg, setBuyingPkg]  = useState(null);   // id while processing
  const [transactions, setTxns]   = useState([]);
  const [features, setFeatures]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [topupSettings, setTopup] = useState({});
  const [saving, setSaving]       = useState(false);

  const animBalance = useAnimatedNumber(wallet?.balance || 0);

  const loadData = useCallback(() => {
    Promise.all([
      walletApi.balance()      .catch(() => null),
      walletApi.packages()     .catch(() => ({ packages: [] })),
      walletApi.transactions() .catch(() => ({ transactions: [] })),
      walletApi.featureCosts() .catch(() => ({ features: [] })),
    ]).then(([w, p, t, f]) => {
      if (w) {
        setWallet(w);
        setTopup({
          low_threshold:              w.low_threshold,
          auto_topup_enabled:         w.auto_topup_enabled,
          auto_topup_package_id:      w.auto_topup_package_id,
          auto_topup_trigger_balance: w.auto_topup_trigger_balance,
        });
      }
      const pkgs = p?.packages || [];
      setPackages(pkgs);
      if (pkgs.length > 1) setSelected(pkgs[1].id); // default popular (index 1)
      else if (pkgs.length > 0) setSelected(pkgs[0].id);
      setTxns(t?.transactions || []);
      setFeatures(f?.features  || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBuy = useCallback(async () => {
    const pkg = packages.find(p => p.id === selectedPkg);
    if (!pkg) return;
    setBuyingPkg(pkg.id);
    try {
      const order  = await walletApi.purchaseOrder(pkg.id);
      const loaded = await loadRazorpay();
      if (!loaded) { alert('Payment gateway failed to load'); setBuyingPkg(null); return; }
      const rzp = new window.Razorpay({
        key:         order.razorpay_key_id,
        amount:      order.amount_paise,
        currency:    order.currency,
        name:        'Click n Go Tokens',
        description: `${order.token_count} tokens — ${order.package_name}`,
        order_id:    order.razorpay_order_id,
        prefill:     {},
        theme:       { color: '#C81E78' },
        handler: async (paymentResponse) => {
          try {
            const result = await walletApi.purchaseConfirm({
              razorpay_order_id:   order.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature:  paymentResponse.razorpay_signature,
            });
            if (result.success) {
              alert(`${result.tokens_credited} tokens added! New balance: ${result.new_balance}`);
              loadData();
            }
          } catch (err) {
            alert(err.message || 'Payment verification failed');
          } finally { setBuyingPkg(null); }
        },
        modal: { ondismiss: () => setBuyingPkg(null) },
      });
      rzp.open();
    } catch (err) {
      alert(err.message || 'Could not initiate payment');
      setBuyingPkg(null);
    }
  }, [selectedPkg, packages, loadData]);

  const saveTopup = async () => {
    setSaving(true);
    try { await walletApi.updateTopup(topupSettings); setWallet(w => ({ ...w, ...topupSettings })); } catch {}
    setSaving(false);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <div style={{ width: 40, height: 40, border: '4px solid var(--line)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes walletPulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );

  const selectedPkgData = packages.find(p => p.id === selectedPkg);
  const publishEstimate  = wallet ? Math.floor(wallet.balance / 500) : 0; // rough: 500 tokens per publish

  const CATEGORY_META = {
    registration: { icon: '🎟', label: 'Registration' },
    email:        { icon: '✉️', label: 'Email'         },
    branding:     { icon: '🎨', label: 'Branding'      },
    analytics:    { icon: '📊', label: 'Analytics'     },
    integration:  { icon: '🔗', label: 'Integration'   },
  };
  const featuresByCategory = features
    // 'registration' is a legacy duplicate of 'attendee_registration' — hide it.
    .filter(f => f.feature_key !== 'registration')
    .reduce((acc, f) => {
      const cat = f.category === 'core' ? 'registration' : (f.category || 'registration');
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(f);
      return acc;
    }, {});

  return (
    <div className="page">

      {/* ── Page head ─────────────────────────────── */}
      <div className="page-head" style={{ marginBottom: 24 }}>
        <div className="page-head-text">
          <h1>Token Wallet</h1>
          <p>Tokens power registrations, reminders &amp; exports. Top up anytime.</p>
        </div>
      </div>

      {/* ── V2 Two-column wallet grid ─────────────── */}
      <div className="wallet-grid">

        {/* ── Left col: balance + pack selector ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Balance card */}
          <div className="balance-card card">
            <div className="glow" />
            <div className="lbl">Available balance</div>
            <div className="big">
              <Coins size={34} color="#FFC83A" strokeWidth={1.75} />
              {animBalance.toLocaleString()}
            </div>
            <div className="meta">
              ≈ {publishEstimate} event publishes · auto-refill {wallet?.auto_topup_enabled ? 'on' : 'off'}
            </div>
            {wallet?.balance <= (wallet?.low_threshold || 500) && (
              <div style={{ fontSize: 13, color: '#FFC83A', marginTop: 10, position: 'relative', animation: 'walletPulse 1.6s ease-in-out infinite' }}>
                ⚠ Balance is low — top up to avoid registration blocks
              </div>
            )}
            <div className="actions">
              <button
                className="btn btn--lg"
                style={{ background: '#fff', color: 'var(--primary-700)' }}
                onClick={() => document.getElementById('wallet-packs')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Plus size={16} strokeWidth={2.5} /> Buy tokens
              </button>
              <button
                className="btn btn--lg btn--dark"
                onClick={() => document.getElementById('wallet-advanced')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <RefreshCw size={16} strokeWidth={1.75} /> Auto-refill
              </button>
            </div>
          </div>

          {/* Pack selector */}
          <div className="card card--pad" id="wallet-packs">
            <div style={{ marginBottom: 4 }}>
              <strong style={{ fontSize: 17, fontWeight: 800 }}>Buy a token pack</strong>
              <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '2px 0 0' }}>
                {selectedPkgData
                  ? `1 token = ₹${(Number(selectedPkgData.price) / selectedPkgData.token_count).toFixed(2)} with the ${selectedPkgData.name || `${selectedPkgData.token_count.toLocaleString()}-token`} pack · GST included`
                  : 'Larger packs cost less per token · GST included'}
              </p>
            </div>

            <div className="pack-grid">
              {packages.map((pkg, i) => (
                <div
                  key={pkg.id}
                  className={`pack${selectedPkg === pkg.id ? ' on' : ''}`}
                  onClick={() => setSelected(pkg.id)}
                >
                  {i === 1 && <div className="tag">Popular</div>}
                  <div className="amt">{pkg.token_count.toLocaleString()}</div>
                  <div className="price">₹{Number(pkg.price).toLocaleString('en-IN')}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                    ₹{(Number(pkg.price) / pkg.token_count).toFixed(2)}/token
                  </div>
                </div>
              ))}
            </div>

            {selectedPkgData && (
              <button className="btn btn--primary btn--block" style={{ marginTop: 16 }} onClick={handleBuy} disabled={!!buyingPkg}>
                {buyingPkg ? '⏳ Processing…' : <><CreditCard size={16} strokeWidth={1.75} /> Pay ₹{Number(selectedPkgData.price).toLocaleString('en-IN')} securely</>}
              </button>
            )}
          </div>
        </div>

        {/* ── Right col: recent activity ─────────── */}
        <div className="card card--pad">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong style={{ fontSize: 17, fontWeight: 800 }}>Recent activity</strong>
            <button className="btn btn--ghost btn--sm">
              <Download size={14} strokeWidth={2} /> Statement
            </button>
          </div>

          {transactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-3)' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🪙</div>
              <div style={{ fontSize: 13 }}>No transactions yet</div>
            </div>
          ) : (
            transactions.slice(0, 12).map((tx, i) => (
              <UsageRow key={tx.id} tx={tx} index={i} />
            ))
          )}
        </div>
      </div>

      {/* ── Platform features + auto top-up (always visible) ────────── */}
      <div style={{ marginTop: 24 }} id="wallet-advanced">
        <div style={{ overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(380px,1fr))', gap: 18, marginTop: 20 }}>

                {/* Feature costs */}
                <div className="card card--pad">
                  <strong style={{ fontSize: 17, fontWeight: 800, display: 'block', marginBottom: 4 }}>Platform features</strong>
                  <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '0 0 14px' }}>
                    Token cost per use. Free features never deduct from your balance.
                  </p>
                  {Object.entries(featuresByCategory).map(([cat, items]) => {
                    const meta = CATEGORY_META[cat] || { icon: '🪙', label: cat };
                    return (
                      <div key={cat} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                          <span>{meta.icon}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-3)' }}>{meta.label}</span>
                        </div>
                        {items.map(feat => (
                          <div key={feat.feature_key} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 0', borderBottom: '1px solid var(--line)',
                          }}>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{feat.label}</div>
                              {feat.description && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{feat.description}</div>}
                            </div>
                            {feat.is_free ? (
                              <span style={{ background: 'var(--success-bg)', color: 'var(--success-ink)', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99 }}>FREE</span>
                            ) : (
                              <span style={{ background: 'var(--violet-50)', color: 'var(--violet)', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99 }}>
                                {feat.token_cost} {feat.token_cost === 1 ? 'token' : 'tokens'}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {features.length === 0 && <p style={{ color: 'var(--ink-3)', textAlign: 'center', padding: '20px 0' }}>No features configured</p>}
                </div>

                {/* Auto top-up settings */}
                <div className="card card--pad">
                  <strong style={{ fontSize: 17, fontWeight: 800, display: 'block', marginBottom: 16 }}>⚙️ Auto top-up</strong>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Auto top-up</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>Top up automatically when balance is low</div>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                      <input type="checkbox" checked={!!topupSettings.auto_topup_enabled}
                        onChange={e => setTopup(s => ({ ...s, auto_topup_enabled: e.target.checked ? 1 : 0 }))}
                        style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{
                        position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 24, transition: '.3s',
                        background: topupSettings.auto_topup_enabled ? 'var(--brand-grad)' : 'var(--line-2)',
                      }}>
                        <span style={{
                          position: 'absolute', height: 18, width: 18, left: 3, bottom: 3,
                          background: 'var(--surface)', borderRadius: '50%', transition: '.3s',
                          transform: topupSettings.auto_topup_enabled ? 'translateX(20px)' : 'translateX(0)',
                        }} />
                      </span>
                    </label>
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 6 }}>Trigger below</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="number" min={0} value={topupSettings.auto_topup_trigger_balance || ''}
                        onChange={e => setTopup(s => ({ ...s, auto_topup_trigger_balance: Number(e.target.value) }))}
                        className="ds-input" style={{ width: 110 }} />
                      <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>tokens</span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: 6 }}>Package</label>
                    <select value={topupSettings.auto_topup_package_id || ''} className="ds-select"
                      onChange={e => setTopup(s => ({ ...s, auto_topup_package_id: Number(e.target.value) }))}>
                      <option value="">Select a package</option>
                      {packages.map(p => (
                        <option key={p.id} value={p.id}>{p.name} — {p.token_count.toLocaleString()} tokens (₹{Number(p.price).toLocaleString('en-IN')})</option>
                      ))}
                    </select>
                  </div>

                  <button className="btn btn--primary" onClick={saveTopup} disabled={saving}>
                    {saving ? 'Saving…' : 'Save settings'}
                  </button>
                </div>
            </div>
          </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes walletPulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
}
